// The Scheduler's placement engine, shared so every screen that answers "who is free,
// and when would this finish?" (the Scheduler board, the Work Orders add-operation
// forms, the work order rows' projected finish) computes it the same way.
//
// Everything here is pure apart from loadScheduleEnv(). Functions take an `env`:
//   { techs, timeOff, duty, mooringDuty, opTypeSkillMap }
// - techs:          buildTechs() output
// - timeOff:        /techTimeOff  — techId -> { 'YYYY-MM-DD': hours available that day }
// - duty:           /haulLaunchOps/duty — 'YYYY-MM-DD' -> { techs: { techId: {...} } }
// - mooringDuty:    /haulLaunchOps/mooringDuty — same shape; the day's mooring (barge) crew
// - opTypeSkillMap: /settings/opTypeSkillMap, if the business saved one

import { idbCursorAll, fetchIdToken, defaultSkillForOpType, TECH_SKILLS } from './utilities';
import { DB_URL } from './firebaseConfig';
import { rememberTechs, shortName } from './techNames';
import { splitHours, taskHoursByDay } from './dayTasksCore';

// Standalone duplicate folder — single Firebase project, no prod/test branching.
const currentURL = DB_URL;

export const CAP = 8;

// Fallback only (see getJobSkill) — for jobs with no usable Op_Type, a skill is
// inferred from free-text Description.
const SKILL_KEYWORDS = {
  Electrical: ['electric', 'wiring', 'inverter', 'battery', 'alternator', 'panel', 'shore power', 'nav electronics', 'generator'],
  Rigging: ['rig', 'winch', 'furler', 'shroud', 'stay', 'standing rigging', 'running rigging', 'mast step'],
  'Wood Work': ['wood', 'rub-rail', 'rub rail', 'teak', 'cabinetry', 'core repair', 'deck repair', 'carpentry'],
  'Bottom Painting': ['bottom paint', 'antifoul', 'barrier coat'],
  Cleaning: ['wash', 'wax', 'compound', 'buff', 'detail', 'topside'],
  Fiberglass: ['fiberglass', 'gelcoat', 'hull repair', 'blister'],
  Moorings: ['mooring', 'pennant', 'mooring chain', 'mooring tackle'],
  Piloting: ['delivery', 'captain', 'piloting', 'sea trial'],
  Launching: ['launch', 'splash', 'commissioning'],
  Hauling: ['haul', 'block', 'travel lift', 'shrinkwrap', 'storage'],
  Mechanical: ['engine', 'repower', 'mechanical', 'impeller', 'cooling', 'outdrive', 'bellows', 'transmission', 'fuel system', 'motor',
    'winteriz', 'inboard', 'outboard', 'stuffing box', 'cutless', 'shaft', 'steering'],
};

const AV_PALETTE = [
  ['#E3EDF7', '#1462B8'], ['#FBEFD8', '#8A5A00'], ['#DDF1EF', '#0B5F58'],
  ['#F7E4DF', '#9A3D2C'], ['#ECE6F6', '#5B3F90'], ['#E3E9F1', '#143B63'],
];

// ---------- techs ----------

// role is a free-form comma list ("tech, yard", "admin, tech", "admin, office") —
// confirmed against live techData records — so membership needs a token check, not
// exact-string equality.
export function hasRoleToken(role, token) {
  return (role || '').split(',').map(s => s.trim().toLowerCase()).includes(token);
}

export function initialsOf(name) {
  return (name || '').split(' ').filter(Boolean).slice(0, 2).map(s => s[0].toUpperCase()).join('');
}

// Real shape (confirmed against live techData): an object of proficiency ratings,
// e.g. { "Bottom Painting": "Low", "Electrical": "High", ... } — every tech has every
// category, most at "Low", so only "High" (or anything above "Low") counts as
// "has this skill" for matching purposes. Array/comma-string kept as a fallback.
export function normalizeSkills(skills) {
  if (Array.isArray(skills)) return skills.map(s => String(s).trim()).filter(Boolean);
  if (skills && typeof skills === 'object') {
    return Object.keys(skills).filter(name => {
      const level = String(skills[name] || '').trim().toLowerCase();
      return level && level !== 'low';
    });
  }
  if (typeof skills === 'string') return skills.split(',').map(s => s.trim()).filter(Boolean);
  return [];
}

export function buildTechs(techRecords) {
  rememberTechs(techRecords); // on-screen names ("Rafael M.") for every page that shows techs
  const techs = techRecords
    .filter(t => hasRoleToken(t.role, 'tech'))
    .map(t => ({
      id: t.id,
      name: (t.displayName || '').split(' ')[0],
      fullName: t.displayName || '',
      label: shortName(t.displayName),
      skills: normalizeSkills(t.skills),
      role: t.role || '',
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  // Jobs store Techs as a first name, so techs sharing one also share every job and
  // their hours double-count. Settings blocks creating that; flag any that exist.
  const firstCounts = {};
  techs.forEach(t => { firstCounts[t.name] = (firstCounts[t.name] || 0) + 1; });
  techs.forEach((t, i) => {
    t.dupFirstName = firstCounts[t.name] > 1;
    const [bg, fg] = AV_PALETTE[i % AV_PALETTE.length];
    t.avBg = bg;
    t.avFg = fg;
    t.initials = initialsOf(t.fullName || t.name);
  });
  return techs;
}

// ---------- skill inference ----------

// The WO form's Op_Type list isn't the tech-skill taxonomy, so each type maps to a
// skill: the business's saved mapping (Settings > Op codes > Skill mapping) first,
// then the shared defaults. A saved '' means "guess from the description".
export function skillForOpType(env, opType) {
  const saved = env.opTypeSkillMap;
  if (saved && Object.prototype.hasOwnProperty.call(saved, opType)) return TECH_SKILLS.includes(saved[opType]) ? saved[opType] : null;
  const d = defaultSkillForOpType(opType);
  return TECH_SKILLS.includes(d) ? d : null;
}

export function keywordSkill(job) {
  const desc = `${job.Description || ''} ${job.Short_Description_DM || job.Short_Description || ''}`.toLowerCase();
  for (const skill of Object.keys(SKILL_KEYWORDS)) {
    if (SKILL_KEYWORDS[skill].some(kw => desc.includes(kw))) return skill;
  }
  return null;
}

// Prefers the job's own Op_Type; the Description keyword guess is only a fallback
// for jobs without one.
export function getJobSkill(env, job) {
  const opType = String(job.Op_Type || '').trim();
  const mapped = opType ? skillForOpType(env, opType) : null;
  if (mapped) {
    // Launches can only be filed under "Hauling" on the WO form.
    if (mapped === 'Hauling' && keywordSkill(job) === 'Launching') return 'Launching';
    return mapped;
  }
  return keywordSkill(job);
}

// ---------- date helpers ----------

export function getTodayString() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().split('T')[0];
}

export function isValidDate(d) {
  return d instanceof Date && !isNaN(d);
}

export function fmtDate(date) {
  return date.toISOString().split('T')[0];
}

export function dateOnly(v) {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : null;
}

// Pure calendar arithmetic in UTC so the result never depends on the browser's timezone.
export function addDays(dateString, n) {
  const d = new Date(`${dateString}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function isSunday(dateString) {
  return new Date(`${dateString}T00:00:00Z`).getUTCDay() === 0;
}

// The engine never rolls work onto Sat/Sun, so planned starts skip them too.
export function nextWorkday(dateString) {
  let ds = dateString;
  while ([0, 6].includes(new Date(`${ds}T00:00:00Z`).getUTCDay())) ds = addDays(ds, 1);
  return ds;
}

export function shortDate(dateString) {
  return new Date(`${dateString}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ---------- capacity per tech per day (time off, haul/launch duty) ----------

// Which full-day crew the office put this tech on for the day, if any:
// 'haulLaunch' (Scheduler > Days, or Haul/Launch > a day's Crew & order panel) or
// 'moorings' (Scheduler > Days > Moorings — the barge crew, Mooring Hauls/Launches).
export function dutyKind(env, techName, dateString) {
  const tech = env.techs.find(t => t.name === techName);
  if (!tech) return null;
  const on = (map) => { const day = map ? map[dateString] : null; return !!(day && day.techs && day.techs[tech.id]); };
  if (on(env.duty)) return 'haulLaunch';
  if (on(env.mooringDuty)) return 'moorings';
  return null;
}

export function isDutyDay(env, techName, dateString) {
  return !!dutyKind(env, techName, dateString);
}

// A tech's hours for one day for jobs: CAP unless a time-off entry says otherwise (0 = off,
// 4 = half day), less the day tasks they're on (env.taskHours, from dayTasksCore.taskHoursByDay:
// battery charging, UST checks, USCG prep…). Time off is stored per tech id at
// /techTimeOff/{uid}/{YYYY-MM-DD}. A haul/launch or mooring crew day is a full day on the crew,
// so nothing else is scheduled onto it.
export function capFor(env, techName, dateString) {
  if (isDutyDay(env, techName, dateString)) return 0;
  const tech = env.techs.find(t => t.name === techName);
  const entry = tech && env.timeOff && env.timeOff[tech.id] ? env.timeOff[tech.id][dateString] : undefined;
  const base = entry == null ? CAP : Math.max(0, Number(entry) || 0);
  return Math.max(0, base - taskHoursFor(env, techName, dateString));
}

/** Hours of day tasks this tech is on that day (0 when the env has none loaded). */
export function taskHoursFor(env, techName, dateString) {
  const tech = env.techs.find(t => t.name === techName);
  const day = env.taskHours && env.taskHours[dateString];
  return (tech && day && Number(day[tech.id])) || 0;
}

// ---------- backlog / active split ----------

export function getBacklogJobs(jobs) {
  return jobs.filter(j => j.Status === 'On Work Schedule' && (!j.Techs || j.Techs.trim() === ''));
}

export function getActiveJobs(jobs, todayString) {
  // Unfinished work from a past day rolls to the next workday — Monday when viewed on a weekend.
  const rollTo = nextWorkday(todayString);
  return jobs
    .filter(j => (j.Status === 'On Work Schedule' || j.Status === 'In Progress') && j.Techs && j.Techs.trim() !== '')
    .sort((a, b) => new Date(a.Start_Date) - new Date(b.Start_Date))
    .map(j => {
      const copy = { ...j };
      if (!copy.Locked && isValidDate(new Date(copy.Schedule_Date)) && new Date(copy.Schedule_Date) < new Date(todayString)) {
        // Rolled forward to the next workday so it stays on the board — but a job that never
        // started by its date is late, and shouldn't silently look on time.
        if (copy.Status === 'On Work Schedule') copy.__lateFrom = copy.Schedule_Date.slice(0, 10);
        copy.Schedule_Date = rollTo;
        copy.Sort_Date = rollTo;
      } else {
        copy.Sort_Date = copy.Schedule_Date;
      }
      return copy;
    });
}

// ---------- engine (ported from app.js initializeTechStats/distributeAndSortJobs) ----------

export function initializeTechStats(techs) {
  const techToday = getTodayString();
  const techStats = {};
  techs.forEach(t => {
    techStats[t.name] = {
      id: t.id,
      totalHours: 0,
      skills: t.skills,
      role: t.role,
      dailyStats: {},
      availableDay: techToday.split('-')[1] + '/' + techToday.split('-')[2],
    };
  });
  return techStats;
}

export function distributeAndSortJobs(env, jobs, techStats) {
  const techToday = getTodayString();
  const firstWorkday = nextWorkday(techToday);
  const yardTechs = env.techs.filter(t => hasRoleToken(t.role, 'yard')).map(t => t.name);

  // Techs normally holds first names, but a full display name ("Ronan Okafor") still
  // means that crew member — resolve it rather than treat it as an unknown tech.
  const byFullName = {};
  const nameById = {};
  env.techs.forEach(t => { if (t.fullName) byFullName[t.fullName.toLowerCase()] = t.name; nameById[t.id] = t.name; });
  const known = new Set(env.techs.map(t => t.name));
  // Tech_IDs (kept in step with Techs server-side by the syncJobTechIds function) only
  // rescues a name that no longer matches anyone, e.g. mid-rename. It never overrides a
  // name that does match, since a fresh reassignment can briefly carry the old IDs.
  const resolveTechNames = (job) => {
    const ids = Array.isArray(job.Tech_IDs) ? job.Tech_IDs : [];
    let names = (job.Techs || '').split(',').map(t => t.trim()).filter(Boolean)
      .map((t, i) => byFullName[t.toLowerCase()] || (!known.has(t) && ids[i] && nameById[ids[i]]) || t);
    if (names.includes('Yard')) names = yardTechs;
    return names;
  };

  for (const job of jobs) {
    const techNames = resolveTechNames(job);
    techNames.forEach(tech => {
      if (!techStats[tech]) {
        techStats[tech] = { totalHours: 0, dailyStats: {}, availableDay: techToday.split('-')[1] + '/' + techToday.split('-')[2] };
      }
    });
  }

  // Locked jobs are packed first and pin their own start day (handoff §4.5) — never
  // clamped to "today" and never pushed later by another job's queue position.
  const lockedJobs = jobs.filter(j => j.Locked && isValidDate(new Date(j.Schedule_Date)));
  // "Put it first" (Scheduler, a drop on a full day): Schedule_First is when the office did it.
  // These go right after the locked jobs, so everything else that would have used that day
  // (its own jobs and work spilling in from earlier days) rolls forward to make room. The
  // newest one goes first when two share a day.
  const firstJobs = jobs.filter(j => !j.Locked && j.Schedule_First && isValidDate(new Date(j.Schedule_Date)))
    .sort((a, b) => (new Date(a.Schedule_Date) - new Date(b.Schedule_Date)) || (Number(b.Schedule_First) - Number(a.Schedule_First)));
  // Every job starts on its own Schedule_Date and only spills forward when that day is
  // full. Earlier days are packed first so a later-dated job can never take capacity
  // from (and push along) work scheduled before it. Stable sort keeps Start_Date order
  // within a day.
  const scheduledJobs = jobs.filter(j => !j.Locked && !j.Schedule_First && isValidDate(new Date(j.Schedule_Date)))
    .sort((a, b) => new Date(a.Schedule_Date) - new Date(b.Schedule_Date));
  const unscheduledJobs = jobs.filter(j => !isValidDate(new Date(j.Schedule_Date)));
  const priorityJobs = new Set([...lockedJobs, ...firstJobs, ...scheduledJobs]);

  for (const job of [...lockedJobs, ...firstJobs, ...scheduledJobs, ...unscheduledJobs]) {
    const techNames = resolveTechNames(job);
    if (techNames.length === 0) continue;

    // Several techs named on one job split its hours (user, 2026-10-24: 4 h with 2 techs is 2 h
    // of each one's day); the "Yard" crew keeps the whole job each, as it always has.
    const named = (job.Techs || '').split(',').map(t => t.trim()).filter(Boolean);
    const total = job.Estimated_Hours ? parseFloat(job.Estimated_Hours) : 0;
    let remainingHours = named.includes('Yard') ? total : splitHours(total, techNames.length);
    // Day stepping is done on YYYY-MM-DD strings (addDays/nextWorkday), never on Date
    // objects — mixing UTC-parsed dates with local getDay() shifted the weekend skip by
    // a day, so Friday overflow landed on Saturday.
    const scheduled = isValidDate(new Date(job.Schedule_Date))
      ? (dateOnly(job.Schedule_Date) || fmtDate(new Date(job.Schedule_Date))) : null;
    // Saturday is only ever used when a job is dropped there directly; overflow,
    // roll-forward and unscheduled work always land on Mon–Fri.
    let dateString;
    if (job.Locked && scheduled) dateString = scheduled;
    else if (!scheduled) dateString = firstWorkday;
    else if (scheduled < techToday) dateString = firstWorkday;
    else dateString = isSunday(scheduled) ? nextWorkday(scheduled) : scheduled;

    // A tech whose every coming day is time off or haul/launch duty would never get
    // the hours placed — stop looking after a year instead of spinning forever.
    let guard = 0;
    while (remainingHours > 0 && guard++ < 366) {
      const availableHours = Math.min(CAP, ...techNames.map(t => capFor(env, t, dateString) - (techStats[t].dailyStats[dateString]?.hours || 0)));
      const hoursToAdd = Math.min(remainingHours, availableHours);

      if (hoursToAdd <= 0) {
        dateString = nextWorkday(addDays(dateString, 1));
        continue;
      }

      techNames.forEach(tech => {
        if (!techStats[tech].dailyStats[dateString]) {
          techStats[tech].dailyStats[dateString] = { hours: 0, jobCount: 0, jobs: [] };
        }
        const seg = { ...job, Estimated_Hours: hoursToAdd, Sort_Date: dateString };
        if (priorityJobs.has(job)) techStats[tech].dailyStats[dateString].jobs.unshift(seg);
        else techStats[tech].dailyStats[dateString].jobs.push(seg);
        techStats[tech].dailyStats[dateString].hours += hoursToAdd;
        techStats[tech].dailyStats[dateString].jobCount++;
        techStats[tech].totalHours += hoursToAdd;
        techStats[tech].availableDay = dateString.split('-')[1] + '/' + dateString.split('-')[2];
      });

      remainingHours -= hoursToAdd;
      if (remainingHours > 0) dateString = nextWorkday(addDays(dateString, 1));
    }
  }

  // Tag part i/n seams: every dailyStats entry sharing an id, in date order, for a tech.
  Object.values(techStats).forEach(stats => {
    const byId = {};
    Object.keys(stats.dailyStats).sort().forEach(dateString => {
      const day = stats.dailyStats[dateString];
      // A job put first is shown at the top of its day (stable sort keeps the rest as packed).
      day.jobs.sort((a, b) => (b.Schedule_First ? 1 : 0) - (a.Schedule_First ? 1 : 0));
      day.jobs.forEach(seg => {
        (byId[seg.id] = byId[seg.id] || []).push(seg);
      });
    });
    Object.values(byId).forEach(segs => {
      segs.forEach((seg, i) => { seg.__part = i + 1; seg.__parts = segs.length; });
    });
  });

  return techStats;
}

// First/last day each scheduled job lands on, from the engine's output.
export function jobSpans(techStats) {
  const spans = {};
  Object.values(techStats).forEach(stats => {
    Object.entries(stats.dailyStats).forEach(([ds, day]) => {
      day.jobs.forEach(seg => {
        const sp = spans[seg.id] || (spans[seg.id] = { first: ds, last: ds, job: seg });
        if (ds < sp.first) sp.first = ds;
        if (ds > sp.last) sp.last = ds;
      });
    });
  });
  return spans;
}

// ---------- "who is free, and when would it finish?" ----------

const SUGGEST_HORIZON_DAYS = 180;

// For a job that doesn't exist yet: where it would land with each tech if it only
// takes hours that are open today — nothing already on the board is moved to make
// room. `from` is the earliest day it may start (defaults to today).
// Returns { skill, begin, rows }, rows being one entry per tech, best pick first:
//   { tech, match, start, end, days, openFirstDay }  (start/end null = no room in the horizon)
export function suggestTechs(env, jobs, draft, from) {
  const hours = parseFloat(draft.Estimated_Hours) || 0;
  const today = getTodayString();
  const begin = nextWorkday(from && from > today ? from : today);
  const skill = getJobSkill(env, draft);
  if (!(hours > 0)) return { skill, begin, rows: [] };
  const stats = distributeAndSortJobs(env, getActiveJobs(jobs, today), initializeTechStats(env.techs));

  const rows = env.techs.map(tech => {
    const daily = (stats[tech.name] && stats[tech.name].dailyStats) || {};
    let left = hours;
    let ds = begin;
    let start = null, end = null, days = 0, openFirstDay = 0;
    for (let i = 0; i < SUGGEST_HORIZON_DAYS && left > 0; i++) {
      const open = capFor(env, tech.name, ds) - ((daily[ds] && daily[ds].hours) || 0);
      if (open > 0.001) {
        if (!start) { start = ds; openFirstDay = open; }
        left -= Math.min(left, open);
        end = ds;
        days++;
      }
      if (left > 0) ds = nextWorkday(addDays(ds, 1));
    }
    if (left > 0) { start = null; end = null; }
    return { tech, match: !!(skill && (tech.skills || []).includes(skill)), start, end, days, openFirstDay };
  });

  rows.sort((a, b) => (b.match - a.match)
    || ((a.end || '9999') < (b.end || '9999') ? -1 : (a.end || '9999') > (b.end || '9999') ? 1 : 0)
    || a.tech.name.localeCompare(b.tech.name));
  return { skill, begin, rows };
}

// Projected finish per work order, from where the engine lands every open operation.
// A work order only gets a date once all its open operations have a tech — otherwise
// the date would quietly leave work out. Map: workOrder -> { end, late }.
export function projectWorkOrders(env, jobs) {
  const today = getTodayString();
  const spans = jobSpans(distributeAndSortJobs(env, getActiveJobs(jobs, today), initializeTechStats(env.techs)));
  const open = {};
  jobs.forEach(j => {
    if (j.Status !== 'On Work Schedule' && j.Status !== 'In Progress') return;
    if (j.Work_Order == null || j.Work_Order === '') return;
    (open[j.Work_Order] = open[j.Work_Order] || []).push(j);
  });
  const out = new Map();
  Object.entries(open).forEach(([wo, ops]) => {
    const placed = ops.map(o => spans[o.id || o.Job_ID]);
    if (placed.some(sp => !sp)) return;
    const end = placed.reduce((m, sp) => (sp.last > m ? sp.last : m), '');
    out.set(String(wo), { end, late: placed.some(sp => !!sp.job.__lateFrom) });
  });
  return out;
}

// ---------- loading ----------

async function fetchJson(path, fallback, query = '') {
  try {
    const idToken = await fetchIdToken();
    const res = await fetch(`${currentURL}/${path}.json?auth=${idToken}${query}`);
    return res.ok ? ((await res.json()) ?? fallback) : fallback;
  } catch (err) {
    console.error(`[schedulerEngine] could not load ${path}`, err);
    return fallback;
  }
}

export const fetchTimeOff = () => fetchJson('techTimeOff', {});
export const fetchDuty = () => fetchJson('haulLaunchOps/duty', {});
export const fetchMooringDuty = () => fetchJson('haulLaunchOps/mooringDuty', {});
export const fetchOpTypeSkillMap = () => fetchJson('settings/opTypeSkillMap', null);

// Day tasks from two weeks back on (they carry over), and the battery charging days the same.
const fromKey = () => { const d = new Date(); d.setDate(d.getDate() - 14); return fmtDate(d); };
const keysFrom = path => fetchJson(path, {}, `&orderBy=${encodeURIComponent('"$key"')}&startAt=${encodeURIComponent(`"${fromKey()}"`)}`);
export const fetchDayTasks = () => keysFrom('dayTasks');
export const fetchBatteryAssign = () => keysFrom('batteries/schedule/assign');

export async function loadScheduleEnv() {
  const [techRecords, timeOff, duty, mooringDuty, opTypeSkillMap, dayTasks, battery] = await Promise.all([
    idbCursorAll('techData'), fetchTimeOff(), fetchDuty(), fetchMooringDuty(), fetchOpTypeSkillMap(), fetchDayTasks(), fetchBatteryAssign(),
  ]);
  return { techs: buildTechs(techRecords), timeOff, duty, mooringDuty, opTypeSkillMap, taskHours: taskHoursByDay(dayTasks, battery) };
}
